import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format } from 'date-fns';
import { FaRegPaste } from 'react-icons/fa6';
import ReactModal from 'react-modal';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Button, ButtonBlack, Input, Select } from '~/components';
import { useAppClipBoardManual } from '~/hooks/clipBoardManual';
import { useDataSheets } from '~/hooks/dataSheets';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import { IDataSheet } from '../../TrainingProgram/interface';
import Modal from '../Modal';

import {
  DuplicateProgramBtn,
  DuplicateProgramContainer,
  FieldSet,
  FieldTitle,
  FormContainer,
  ModalContainer,
  RadioInput,
} from './styles';

type Props = {
  billingId: number;
  studentId: number;
  programId: number;
  refreshProgram: () => Promise<void>;
};

const SheetModelModalView = ({
  billingId,
  studentId,
  programId,
  refreshProgram,
}: Props) => {
  const formRef = useRef<FormHandles>(null);

  const { loadDataSheets, dataSheetsList } = useDataSheets();
  const { textCliped } = useAppClipBoardManual();
  const { goBack } = useHistory();

  const [loading, setLoading] = useState(false);
  const [sheetModel, setSheetModel] = useState('Basic');
  const [sheetModelModal, setSheetModelModal] = useState(true);
  const [isPossibleToDuplicate, setIsPossibleToDuplicate] = useState(false);
  const [_modelDataSheet, set_ModelDataSheet] = useState('');
  const [alertModal, setAlertModal] = useState(false);

  const dataSheetsMemo = useMemo(
    () =>
      dataSheetsList.map((dataSheet: IDataSheet) => ({
        value: dataSheet.id,
        label: format(new Date(dataSheet.created_at), 'dd/MM/yyyy'),
      })),
    [dataSheetsList],
  );

  useEffect(() => {
    loadDataSheets(String(studentId));
  }, [loadDataSheets, studentId]);

  const radioSheetModelOptionsList = useMemo(
    () => [
      { value: 'Basic', label: 'Basic' },
      { value: 'Premium', label: 'Premium' },
      { value: 'MasterClass', label: 'MasterClass' },
    ],
    [],
  );

  const RadioSheetModelOptions = useMemo(
    () =>
      radioSheetModelOptionsList.map((item) => (
        <RadioInput
          key={item.label}
          isSelected={item.value === sheetModel}
          onClick={() => setSheetModel(item.value)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [radioSheetModelOptionsList, sheetModel],
  );

  const selectStyle = {
    placeholder: () => ({
      padding: '0 1.7rem',
      color: '#00000080',
    }),
    singleValue: () => ({
      padding: '0 1rem',
    }),
  };

  const inputStyle = {
    height: 35,
  };

  const handleSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true);

      const formData = {
        ...data,
        data_model: sheetModel,
        billing_id: Number(billingId),
        student_id: Number(studentId),
        program_id: Number(programId),
      };

      const schema = Yup.object().shape({
        billing_id: Yup.number().required('O campo é obrigatório!'),
        data_model: Yup.string().required('O campo é obrigatório!'),
        program_id: Yup.number().required('O campo é obrigatório!'),
        student_id: Yup.number().required('O campo é obrigatório!'),
        training_division: Yup.string().required('O campo é obrigatório!'),
        datasheet_id: Yup.number().required('O campo é obrigatório!'),
      });

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.post('/training-programs', formData);

        toast.success('Novo programa cadastrado com sucesso!');

        await refreshProgram();

        setSheetModelModal(false);

        setLoading(false);
      } catch (err) {
        console.log(err);

        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar novo programa!');
      }
    },
    [billingId, programId, refreshProgram, sheetModel, studentId],
  );

  const formatingTraningDivisionText = (
    value: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const formattedValue = value.target.value;
    const txtFaseOne = formattedValue.replace(/,|\d+$|([H-Z])|([h-z])/gm, '');
    const txtFaseTwo = txtFaseOne.split('');
    const txtFaseThree = txtFaseTwo.join();
    const txtFaseFour = txtFaseThree.toUpperCase();

    formRef?.current?.setFieldValue('training_division', txtFaseFour);
  };

  async function handleDuplicateTrainingProgram() {
    try {
      const text = textCliped;
      const vector = text.split('/');
      const copied_training_id = vector[0];
      const datasheetId = vector[1];

      await api.get(
        `training-programs/all/copy?copied_training_id=${copied_training_id}&program_id=${programId}&datasheet_id=${datasheetId}`,
      );

      toast.success('Programa duplicado com sucesso!');
      setAlertModal(false);
      refreshProgram();
    } catch (error: any) {
      console.log(error);
      toast.error('Houve um erro ao duplicar o programa');
    }
  }

  function showAlert() {
    return (
      <ReactModal
        isOpen={alertModal}
        shouldCloseOnOverlayClick
        ariaHideApp={false}
        style={{
          content: {
            overflow: 'auto',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            background: '#FFFEFC',
            color: '#000000',
            borderRadius: '0.8rem',
            maxWidth: 550,
            width: '100%',
            maxHeight: 260,
            height: '100%',
            animation: 'bounceIn 1s ease-in-out',
            boxShadow: '#0000000F 0px 0.3rem 0.6rem',
            padding: 0,
          },
          overlay: {
            background: '#232323aa',
            zIndex: 120,
            transition: 'none',
          },
        }}
      >
        <DuplicateProgramContainer>
          <h1>Duplicar modelo de treino?</h1>
          <p>
            Ao Duplicar o modelo de treino os dados desse novo treino será como
            o do modelo copiado anteriormente!
          </p>

          <div>
            <ButtonBlack>Cancelar</ButtonBlack>
            <Button onClick={handleDuplicateTrainingProgram}>OK</Button>
          </div>
        </DuplicateProgramContainer>
      </ReactModal>
    );
  }

  async function isPossibleToDuplicateTraining() {
    try {
      const text = textCliped;
      const vector = text.split('/');
      const isNan = Number.isNaN(Number(vector[0]));

      if (isNan) {
        // console.log('NADA COPIADO');
        setIsPossibleToDuplicate(false);
        set_ModelDataSheet('');
      } else {
        if (vector[2] === sheetModel) {
          // console.log('OS TIPOS DE FICHAS SAO IGUAIS');
          set_ModelDataSheet(vector[2]);
          setIsPossibleToDuplicate(true);
        }

        if (vector[2] !== sheetModel) {
          // console.log('OS TIPOS DE FICHAS NAO SAO IGUAIS');
          set_ModelDataSheet('');
          setIsPossibleToDuplicate(false);
        }
      }
    } catch (error) {
      console.log('error:', error);
    }
  }

  useEffect(() => {
    isPossibleToDuplicateTraining();
  }, [sheetModel]);

  return (
    <Modal isOpen={sheetModelModal} maxWidthStyled={621} maxHeightStyled={600}>
      <ModalContainer>
        <h1>Modelo de ficha</h1>

        <FieldSet>
          <div>{RadioSheetModelOptions}</div>
        </FieldSet>

        <FormContainer>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <FieldSet>
              <FieldTitle secondary>Ficha técnica:</FieldTitle>
              <Select
                name="datasheet_id"
                placeholder="Selecionar"
                isSearchable={false}
                defaultValue={dataSheetsMemo[0]}
                options={dataSheetsMemo}
                styles={selectStyle}
              />
            </FieldSet>

            {isPossibleToDuplicate && _modelDataSheet === sheetModel && (
              <>
                <DuplicateProgramBtn
                  type="button"
                  onClick={() => setAlertModal(true)}
                >
                  <FaRegPaste />
                  Duplicar programa de treino
                </DuplicateProgramBtn>

                {showAlert()}
              </>
            )}

            <FieldSet>
              <FieldTitle secondary>Divisão de treino (A, B, C...):</FieldTitle>
              <Input
                name="training_division"
                placeholder="exemplo: A,B,C..."
                onChange={formatingTraningDivisionText}
                style={inputStyle}
              />
            </FieldSet>
          </Form>
        </FormContainer>

        <ButtonBlack
          disabled={loading}
          onClick={() => formRef.current?.submitForm()}
        >
          Selecionar
        </ButtonBlack>
      </ModalContainer>
    </Modal>
  );
};

export default SheetModelModalView;
